import { useEffect, useMemo, useState } from 'react'
import { Search, Link2, UserPlus, Loader2, Info } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Modal } from '@/components/ui/Modal'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { AsyncSelect, type AsyncOption } from '@/components/ui/AsyncSelect'
import { InfoPopover } from '@/components/ui/InfoPopover'
import { api } from '@/services/api/AxiosBaseService'
import { CompaniesService } from '@/services/modules/CompaniesService'
import { AsaasIntegrationService, type AsaasChargeRow, type AsaasCustomerRow } from '@/services/modules/AsaasIntegrationService'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'
import { exportToXlsx, type ExportColumn } from '@/lib/exportXlsx'
import { useAuthority } from '@/hooks/useAuthority'

function currentMonthRange() {
  const now = new Date()
  const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  return { from: fmt(new Date(now.getFullYear(), now.getMonth(), 1)), to: fmt(new Date(now.getFullYear(), now.getMonth() + 1, 0)) }
}
const brl = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const ymd = (s: string | null) => (s ? s.split('-').reverse().join('/') : '—')

// Tradução amigável do tipo de cobrança e do status do Asaas.
const BILLING_LABEL: Record<string, string> = {
  BOLETO: 'Boleto', PIX: 'Pix', CREDIT_CARD: 'Cartão de Crédito', DEBIT_CARD: 'Cartão de Débito', UNDEFINED: '—',
}
const billingLabel = (b: string | null) => (b ? (BILLING_LABEL[b] ?? b) : '—')

type BadgeVariant = 'green' | 'amber' | 'red' | 'gray' | 'blue' | 'violet'
const STATUS_META: Record<string, { label: string; variant: BadgeVariant }> = {
  PENDING:                { label: 'Pendente',              variant: 'amber' },
  RECEIVED:               { label: 'Recebido',              variant: 'green' },
  CONFIRMED:              { label: 'Confirmado',            variant: 'green' },
  RECEIVED_IN_CASH:       { label: 'Recebido em dinheiro',  variant: 'green' },
  OVERDUE:                { label: 'Vencido',               variant: 'red' },
  REFUNDED:               { label: 'Reembolsado',           variant: 'gray' },
  REFUND_REQUESTED:       { label: 'Reembolso solicitado',  variant: 'gray' },
  CHARGEBACK_REQUESTED:   { label: 'Chargeback solicitado', variant: 'red' },
  CHARGEBACK_DISPUTE:     { label: 'Disputa de chargeback', variant: 'red' },
  AWAITING_RISK_ANALYSIS: { label: 'Em análise de risco',   variant: 'blue' },
  AWAITING_CHARGEBACK_REVERSAL: { label: 'Aguardando estorno', variant: 'amber' },
  DUNNING_REQUESTED:      { label: 'Em negativação',        variant: 'amber' },
  DUNNING_RECEIVED:       { label: 'Recuperada',            variant: 'green' },
}
const statusMeta = (s: string | null): { label: string; variant: BadgeVariant } =>
  (s && STATUS_META[s]) || { label: s ?? '—', variant: 'gray' }

const docType = (cpfCnpj: string | null) => ((cpfCnpj ?? '').replace(/\D+/g, '').length === 14 ? 'Empresa' : 'Associado')

const CHARGE_EXPORT_COLUMNS: ExportColumn<AsaasChargeRow>[] = [
  { header: 'Cliente',           accessor: (c) => c.customerName ?? '' },
  { header: 'Situação do cliente', accessor: (c) => (c.clientId ? (c.clientType === 'company' ? 'Empresa no sistema' : 'Associado no sistema') : 'Cliente fora do sistema') },
  { header: 'Cliente Asaas',     accessor: (c) => c.customerId ?? '' },
  { header: 'Cobrança Asaas',    accessor: (c) => c.paymentId },
  { header: 'Descrição',         accessor: (c) => c.description ?? '' },
  { header: 'Forma de pagamento', accessor: (c) => (c.billingType ? billingLabel(c.billingType) : '') },
  { header: 'Status',            accessor: (c) => (c.status ? statusMeta(c.status).label : '') },
  { header: 'Vencimento',        accessor: (c) => (c.dueDate ? ymd(c.dueDate) : '') },
  { header: 'Valor',             accessor: (c) => brl(c.value) },
]

const CUSTOMER_EXPORT_COLUMNS: ExportColumn<AsaasCustomerRow>[] = [
  { header: 'Cliente',       accessor: (c) => c.name },
  { header: 'CPF/CNPJ',      accessor: (c) => c.cpfCnpj ?? '' },
  { header: 'E-mail',        accessor: (c) => c.email ?? '' },
  { header: 'Tipo',          accessor: (c) => docType(c.cpfCnpj) },
  { header: 'Cobranças',     accessor: (c) => c.charges },
  { header: 'Cliente Asaas', accessor: (c) => c.customerId },
]

const VIEWS = [
  { key: 'charges', label: 'Cobranças fora do sistema' },
  { key: 'customers', label: 'Clientes fora do sistema' },
] as const
type ViewKey = (typeof VIEWS)[number]['key']

export function AsaasChargeFinderPage() {
  const init = useMemo(currentMonthRange, [])
  const [from, setFrom] = useState(init.from)
  const [to, setTo] = useState(init.to)
  const [view, setView] = useState<ViewKey>('charges')
  const [loading, setLoading] = useState(false)
  const [charges, setCharges] = useState<AsaasChargeRow[]>([])
  const [customers, setCustomers] = useState<AsaasCustomerRow[]>([])
  const [linkTarget, setLinkTarget] = useState<AsaasChargeRow | null>(null)
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const { can } = useAuthority()
  const canLink = can('integrations.asaas.charge_link')
  const canImport = can('integrations.asaas.import_customer')

  async function search() {
    if (from > to) { toast.error({ title: 'Período inválido', description: 'A data inicial não pode ser maior que a final.' }); return }
    setLoading(true)
    try {
      const res = await AsaasIntegrationService.chargeFinder(from, to)
      setCharges(res.chargesNotInSystem)
      setCustomers(res.customersNotInSystem)
      if (res.meta.fake) toast.info({ title: 'Asaas em modo teste', description: 'Sem credenciais reais — nada a buscar.' })
    } catch (e) {
      toast.error({ title: 'Erro na busca', description: extractApiError(e).message })
    } finally { setLoading(false) }
  }
  useEffect(() => { search() /* primeira carga */ /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [])

  // Vincula uma cobrança a um cliente (auto-detectado ou escolhido).
  async function linkCharge(c: AsaasChargeRow, clientType: 'subscriber' | 'company', clientId: string) {
    setBusyKey(c.paymentId)
    try {
      await AsaasIntegrationService.linkCharge({
        payment_id: c.paymentId,
        subscriber_id: clientType === 'subscriber' ? clientId : null,
        company_id: clientType === 'company' ? clientId : null,
      })
      toast.success({ title: 'Cobrança vinculada', description: 'A fatura foi criada e vinculada.' })
      setCharges((prev) => prev.filter((x) => x.paymentId !== c.paymentId))
      setLinkTarget(null)
    } catch (e) {
      toast.error({ title: 'Não foi possível vincular', description: extractApiError(e).message })
    } finally { setBusyKey(null) }
  }

  // Cria o cliente no sistema e vincula automaticamente todas as cobranças dele.
  async function importCustomer(cust: AsaasCustomerRow) {
    setBusyKey(cust.customerId)
    try {
      const res = await AsaasIntegrationService.importCustomer({ customer_id: cust.customerId })
      const clientType: 'subscriber' | 'company' = res.companyId ? 'company' : 'subscriber'
      const clientId = res.companyId ?? res.subscriberId
      let linked = 0
      if (clientId) {
        const own = charges.filter((x) => x.customerId === cust.customerId)
        for (const ch of own) {
          try {
            await AsaasIntegrationService.linkCharge({
              payment_id: ch.paymentId,
              subscriber_id: clientType === 'subscriber' ? clientId : null,
              company_id: clientType === 'company' ? clientId : null,
            })
            linked++
          } catch { /* segue */ }
        }
      }
      toast.success({ title: 'Cliente adicionado', description: `${cust.name} cadastrado. ${linked} cobrança(s) vinculada(s).` })
      setCustomers((prev) => prev.filter((x) => x.customerId !== cust.customerId))
      setCharges((prev) => prev.filter((x) => x.customerId !== cust.customerId))
    } catch (e) {
      toast.error({ title: 'Não foi possível adicionar', description: extractApiError(e).message })
    } finally { setBusyKey(null) }
  }

  function exportCharges() {
    if (!charges.length) { toast.error('Nenhum registro para exportar.'); return }
    exportToXlsx('cobrancas-fora-do-sistema', charges, CHARGE_EXPORT_COLUMNS)
  }

  function exportCustomers() {
    if (!customers.length) { toast.error('Nenhum registro para exportar.'); return }
    exportToXlsx('clientes-fora-do-sistema', customers, CUSTOMER_EXPORT_COLUMNS)
  }

  const chargeCols: DataTableColumn<AsaasChargeRow>[] = [
    { key: 'customer', header: 'Cliente', alwaysVisible: true, render: (c) => (
      <div className="min-w-0">
        <div className="break-words font-medium text-gray-800">{c.customerName ?? '—'}</div>
        <div className="mt-0.5">
          {c.clientId
            ? <Badge variant={c.clientType === 'company' ? 'blue' : 'gray'}>{c.clientType === 'company' ? 'Empresa no sistema' : 'Associado no sistema'}</Badge>
            : <span className="text-[11px] text-amber-600">cliente fora do sistema</span>}
        </div>
      </div>
    ) },
    { key: 'desc', header: 'Cobrança', className: 'w-80', render: (c) => {
      const full = c.description || c.paymentId
      const long = full.length > 65
      return (
        <div className="min-w-0">
          <div className="flex items-start gap-1 text-sm text-gray-700">
            <span>{long ? `${full.slice(0, 65)}…` : full}</span>
            {long && (
              <InfoPopover align="left" ariaLabel="Descrição completa" title="Descrição"
                trigger={<Info size={13} className="mt-0.5 shrink-0 cursor-pointer text-gray-400 hover:text-brand-500" />}>
                <p className="max-w-xs whitespace-pre-wrap break-words text-xs text-gray-700">{full}</p>
              </InfoPopover>
            )}
          </div>
          <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-gray-500">
            <span>{billingLabel(c.billingType)}</span><span className="text-gray-300">·</span>
            <Badge variant={statusMeta(c.status).variant}>{statusMeta(c.status).label}</Badge>
          </div>
        </div>
      )
    } },
    { key: 'dueDate', header: 'Vencimento', align: 'center', className: 'w-28', render: (c) => ymd(c.dueDate) },
    { key: 'value', header: 'Valor', align: 'center', className: 'w-28', render: (c) => brl(c.value) },
    ...(canLink ? [{ key: 'actions', header: 'Ação', align: 'center', className: 'w-48', render: (c: AsaasChargeRow) => (
      c.clientId && c.clientType
        ? <Button size="sm" iconLeft={<Link2 size={13} />} loading={busyKey === c.paymentId} onClick={() => linkCharge(c, c.clientType!, c.clientId!)}>Vincular a {c.clientType === 'company' ? 'empresa' : 'associado'}</Button>
        : <Button size="sm" variant="secondary" iconLeft={<Link2 size={13} />} onClick={() => setLinkTarget(c)}>Selecionar cliente</Button>
    ) } as DataTableColumn<AsaasChargeRow>] : []),
  ]

  const customerCols: DataTableColumn<AsaasCustomerRow>[] = [
    { key: 'name', header: 'Cliente', alwaysVisible: true, render: (c) => (
      <div className="min-w-0">
        <div className="break-words font-medium text-gray-800">{c.name}</div>
        <div className="text-[11px] text-gray-400">{c.cpfCnpj ?? '—'} · {c.email ?? '—'}</div>
      </div>
    ) },
    { key: 'doc', header: 'Tipo', align: 'center', className: 'w-28', render: (c) => (
      <Badge variant={(c.cpfCnpj ?? '').replace(/\D+/g, '').length === 14 ? 'blue' : 'gray'}>
        {(c.cpfCnpj ?? '').replace(/\D+/g, '').length === 14 ? 'Empresa' : 'Associado'}
      </Badge>
    ) },
    { key: 'charges', header: 'Cobranças', align: 'center', className: 'w-28', render: (c) => c.charges },
    ...(canImport ? [{ key: 'actions', header: '', align: 'center', className: 'w-52', render: (c: AsaasCustomerRow) => (
      <Button size="sm" iconLeft={<UserPlus size={13} />} loading={busyKey === c.customerId} onClick={() => importCustomer(c)}>Adicionar e vincular</Button>
    ) } as DataTableColumn<AsaasCustomerRow>] : []),
  ]

  return (
    <>
      <PageHeader
        title="Buscador de cobrança"
        subtitle="Busca cobranças no Asaas (por vencimento) e mostra o que está fora do sistema"
        crumbs={[{ label: 'Integrações' }, { label: 'Asaas' }, { label: 'Buscador de cobrança' }]}
      />

      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-md bg-white p-4 ring-1 ring-gray-200">
        <Input label="De" type="date" value={from} onChange={(e) => setFrom(e.target.value)} wrapperClassName="w-44" />
        <Input label="Até" type="date" value={to} onChange={(e) => setTo(e.target.value)} wrapperClassName="w-44" />
        <Button onClick={search} loading={loading} iconLeft={<Search size={14} />}>Buscar</Button>
      </div>

      {/* Grupo de visões */}
      <div className="mb-4 grid w-full max-w-lg grid-cols-2 overflow-hidden rounded-md ring-1 ring-gray-200">
        {VIEWS.map((v) => (
          <button key={v.key} onClick={() => setView(v.key)}
            className={`cursor-pointer px-3 py-1.5 text-center text-sm transition ${view === v.key ? 'bg-brand-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
            {v.label} {v.key === 'charges' ? `(${charges.length})` : `(${customers.length})`}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid h-40 place-items-center text-gray-400"><Loader2 className="animate-spin" /></div>
      ) : view === 'charges' ? (
        <DataTable tableId="asaas.charges-out" exportable onExportAll={exportCharges} columns={chargeCols} rows={charges} getRowKey={(c) => c.paymentId}
          emptyMessage="Nenhuma cobrança fora do sistema no período." />
      ) : (
        <DataTable tableId="asaas.customers-out" exportable onExportAll={exportCustomers} columns={customerCols} rows={customers} getRowKey={(c) => c.customerId}
          emptyMessage="Nenhum cliente fora do sistema no período." />
      )}

      {linkTarget && (
        <LinkClientModal charge={linkTarget} busy={busyKey === linkTarget.paymentId}
          onClose={() => setLinkTarget(null)}
          onConfirm={(type, id) => linkCharge(linkTarget, type, id)} />
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Modal de seleção manual de cliente (associado PF ou empresa).
// ---------------------------------------------------------------------------
function LinkClientModal({ charge, busy, onClose, onConfirm }: {
  charge: AsaasChargeRow
  busy: boolean
  onClose: () => void
  onConfirm: (type: 'subscriber' | 'company', id: string) => void
}) {
  const [type, setType] = useState<'subscriber' | 'company'>('subscriber')
  const [id, setId] = useState('')
  const [label, setLabel] = useState('')
  const [companies, setCompanies] = useState<{ id: string; name: string }[]>([])

  useEffect(() => {
    if (type === 'company' && companies.length === 0) {
      CompaniesService.list({ size: 0, status: 'ATIVO' } as never)
        .then((r) => setCompanies(r.data.map((c) => ({ id: c.id, name: c.name }))))
        .catch(() => setCompanies([]))
    }
    setId(''); setLabel('')
  }, [type]) // eslint-disable-line react-hooks/exhaustive-deps

  async function loadSubscribers(q: string): Promise<AsyncOption[]> {
    const r = await api.get('/commercial/subscribers/simple-list', { params: { q } })
    return (r.data?.data ?? []).map((s: { id: string; name: string; cpf?: string }) => ({ value: s.id, label: `${s.name}${s.cpf ? ` · ${s.cpf}` : ''}` }))
  }
  async function loadCompanies(q: string): Promise<AsyncOption[]> {
    const n = q.trim().toLowerCase()
    return companies.filter((c) => !n || c.name.toLowerCase().includes(n)).slice(0, 30).map((c) => ({ value: c.id, label: c.name }))
  }

  return (
    <Modal open onClose={onClose} title="Vincular cobrança a um cliente" size="sm"
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button onClick={() => id && onConfirm(type, id)} loading={busy} disabled={!id}>Vincular</Button>
      </>}>
      <div className="space-y-3">
        <p className="text-xs text-gray-500">{charge.description || charge.paymentId} · {brl(charge.value)} · venc. {ymd(charge.dueDate)}</p>
        <div className="grid grid-cols-2 overflow-hidden rounded-md ring-1 ring-gray-200">
          {(['subscriber', 'company'] as const).map((t) => (
            <button key={t} onClick={() => setType(t)}
              className={`cursor-pointer px-3 py-1.5 text-center text-sm transition ${type === t ? 'bg-brand-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              {t === 'subscriber' ? 'Associado (PF)' : 'Empresa (PJ)'}
            </button>
          ))}
        </div>
        <AsyncSelect
          label={type === 'subscriber' ? 'Associado' : 'Empresa'}
          value={id}
          selectedLabel={label}
          loadOptions={type === 'subscriber' ? loadSubscribers : loadCompanies}
          onChange={(v, opt) => { setId(v); setLabel(opt?.label ?? '') }}
          placeholder="Selecione o cliente"
        />
      </div>
    </Modal>
  )
}

export default AsaasChargeFinderPage
